/**
 * Core, hidden chat capabilities. These names must never be added to ToolId or
 * the database-backed tools catalogue: they are product actions, not tools a
 * user enables from agent settings.
 */
export const DATABASE_UUID_PATTERN =
  /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

export const WOODS_ACTION_NAMES = [
  "list_agents",
  "get_agent_details",
  "create_agent",
  "update_agent",
  "deactivate_current_agent",
  "activate_agent",
  "list_projects",
  "search_my_projects",
  "search_my_chats",
  "list_teams",
  "get_woods_help",
  "search_meetings",
  "prepare_meeting",
  "list_knowledge_files",
  "save_chat_file_to_knowledge_base",
  "attach_file_to_project",
  "request_file_team_transfer",
  "move_current_chat_to_project",
] as const;

export type WoodsActionName = (typeof WOODS_ACTION_NAMES)[number];

export type MovedChatDestination = {
  projectChatId: string;
  projectId: string;
};

export const WOODS_STRUCTURED_RESULT_ACTION_NAMES = [
  "list_agents",
  "get_agent_details",
  "list_projects",
  "search_my_projects",
  "search_my_chats",
  "list_teams",
  "search_meetings",
  "list_knowledge_files",
] as const satisfies readonly WoodsActionName[];

export const WOODS_WRITE_ACTION_NAMES = [
  "create_agent",
  "update_agent",
  "deactivate_current_agent",
  "activate_agent",
  "prepare_meeting",
  "save_chat_file_to_knowledge_base",
  "attach_file_to_project",
  "request_file_team_transfer",
  "move_current_chat_to_project",
] as const satisfies readonly WoodsActionName[];

const WOODS_ACTION_NAME_SET = new Set<string>(WOODS_ACTION_NAMES);
const WOODS_WRITE_ACTION_NAME_SET = new Set<string>(WOODS_WRITE_ACTION_NAMES);
const WOODS_STRUCTURED_RESULT_ACTION_NAME_SET = new Set<string>(WOODS_STRUCTURED_RESULT_ACTION_NAMES);

export const isWoodsActionName = (value: string): value is WoodsActionName =>
  WOODS_ACTION_NAME_SET.has(value);

export const isWoodsWriteActionName = (value: string): value is (typeof WOODS_WRITE_ACTION_NAMES)[number] =>
  WOODS_WRITE_ACTION_NAME_SET.has(value);

export const isWoodsStructuredResultActionName = (
  value: string
): value is (typeof WOODS_STRUCTURED_RESULT_ACTION_NAMES)[number] =>
  WOODS_STRUCTURED_RESULT_ACTION_NAME_SET.has(value);

/** Discovery stays available to the model without displaying unsolicited cards.
 * Missing flags (including saved legacy calls) default to hidden. Help-doc
 * lookups are background reading for the answer, so they never render.
 */
export function shouldShowWoodsAction(name: string, input: unknown): boolean {
  if (name === "get_woods_help") return false;
  if (!isWoodsStructuredResultActionName(name)) return true;
  return typeof input === "object" && input !== null &&
    "show_results" in input && input.show_results === true;
}

/**
 * Find a successful move-to-project result in the current assistant turn.
 * Both the browser and the resumable-stream persistence path use this handoff
 * so they stop writing to the personal chat row that the move deleted. Older
 * results must never be reused: they may point at a destination that no longer
 * exists and would otherwise trigger navigation after every later response.
 */
export function getMovedChatDestination(
  messages: readonly { role?: string; parts?: readonly unknown[] }[]
): MovedChatDestination | null {
  const currentAssistant = [...messages]
    .reverse()
    .find((message) => message.role === "assistant");
  if (!currentAssistant?.parts) return null;

  for (const value of [...currentAssistant.parts].reverse()) {
    if (!value || typeof value !== "object") continue;
    const part = value as {
      type?: string;
      toolName?: string;
      state?: string;
      output?: unknown;
      approval?: { approved?: boolean };
    };
    const name = part.type === "dynamic-tool"
      ? part.toolName
      : part.type?.replace(/^tool-/, "");
    if (
      name !== "move_current_chat_to_project" ||
      part.state !== "output-available" ||
      part.approval?.approved !== true ||
      !part.output ||
      typeof part.output !== "object"
    ) {
      continue;
    }

    const output = part.output as Record<string, unknown>;
    if (
      output.moved === true &&
      typeof output.projectChatId === "string" &&
      typeof output.projectId === "string"
    ) {
      return {
        projectChatId: output.projectChatId,
        projectId: output.projectId,
      };
    }
  }

  return null;
}
